import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { MessageSquare, PenTool, Code2, Rocket } from 'lucide-react';
import './HowItWorks.css';

const steps = [
  {
    n: '01',
    icon: MessageSquare,
    title: 'Scope',
    desc: 'One call to understand your business, use case, users, and existing systems. No fluff.',
  },
  {
    n: '02',
    icon: PenTool,
    title: 'Design',
    desc: 'We architect the agent — flows, integrations, fallbacks, escalation paths. You approve before we build.',
  },
  {
    n: '03',
    icon: Code2,
    title: 'Build & Test',
    desc: 'Production-grade development, tested against real scenarios, integrated into your stack.',
  },
  {
    n: '04',
    icon: Rocket,
    title: 'Deploy & Support',
    desc: 'Live in production. Monitored. We stay on for support and iteration.',
  },
];

export const HowItWorks = () => {
  const reduced = useReducedMotion();

  const reveal = {
    hidden: { opacity: 0, y: reduced ? 0 : 24 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0.2 : 0.55, delay: reduced ? 0 : i * 0.1, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <section className="how-section">
      <div className="how-container">
        <motion.h2
          className="how-title"
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          From idea to deployed agent — fast.
        </motion.h2>

        <div className="how-steps">
          <motion.div
            className="how-line"
            initial={{ scaleX: reduced ? 1 : 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: reduced ? 0.2 : 0.9, ease: [0.16, 1, 0.3, 1] }}
            aria-hidden="true"
          />
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.n}
                className="how-step"
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                custom={i}
              >
                <div className="how-node">
                  <Icon size={22} />
                  <span className="how-num">{step.n}</span>
                </div>
                <h3 className="how-step-title">{step.title}</h3>
                <p className="how-step-desc">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
